import { useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "./apiClient";
import {
  IBranchesResponse,
  ICitiesResponse,
  ICurrenciesResponse,
  IPaymentsResponse,
  ICountriesResponse,
} from "src/types/generic";

const ONE_DAY = 1000 * 60 * 60 * 24;

export const useGetCountries = () =>
  useQuery<any, any, ICountriesResponse>({
    queryKey: ["get-countries"],
    queryFn: async () => {
      return apiClient.get(`/api/countries`);
    },
    staleTime: ONE_DAY,
    gcTime: ONE_DAY,
  });

export const useGetCities = () =>
  useQuery<any, any, ICitiesResponse>({
    queryKey: ["get-cities"],
    queryFn: async () => {
      return apiClient.get(`/api/cities`);
    },
    staleTime: ONE_DAY,
    gcTime: ONE_DAY,
  });

const ONE_HOUR = 1000 * 60 * 60;

export const useGetBranches = () =>
  useQuery<any, any, IBranchesResponse>({
    queryKey: ["get-branches"],
    queryFn: async () => {
      return apiClient.get(`/api/branches`);
    },
    gcTime: ONE_HOUR,
  });

export const useGetPaymentMethods = (params?: {
  branches?: number[];
  page?: number;
  is_aggregator?: 1 | 0;
  enabled?: boolean;
}) =>
  useQuery<any, any, IPaymentsResponse>({
    queryKey: ["get-payments-methods", params?.page, params?.branches],
    queryFn: async () => {
      return apiClient.get(`/api/paymentMethods`, { params: params || {} }); // If no params, it returns all payments
    },
    gcTime: 0,
    enabled: params?.enabled ?? true,
  });

export const useGetJourneys = (params: {
  branches: number[];
  enabled: boolean;
}) =>
  useQuery<any, any, any>({
    queryKey: ["get-journeys", params.branches.map((branch) => branch)],
    queryFn: async () => {
      return apiClient.get(`/api/journeys/get-journey-filter`, {
        params:
          params?.branches.length > 0 ? { branches: params?.branches } : {},
      }); // If no params, it returns all journeys
    },
    enabled: params?.enabled,
    gcTime: 0,
  });

interface ICompanyDetails {
  id: number | string;
  close_date?: string;
  foodics_reference?: string;
  government_tax?: number;
  start_balance_date?: string;
  tax_registration_number?: string;
  commercial_registration_number?: string;
  address?: {
    street_address: string;
    building_number: string;
    district: string;
    city_id: string | number;
    country_id: string | number;
    postal_code: string;
    secondary_number?: string;
  };
}

export const useUpdateCompanyData = () =>
  useMutation<void, any, ICompanyDetails>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/companies/update-company-data/${id}`, rest);
    },
  });

export const useGetCurrencies = () =>
  useQuery<any, any, ICurrenciesResponse>({
    queryKey: ["get-currencies"],
    queryFn: async () => {
      return apiClient.get(`/api/currencies`);
    },
  });
